/**
 * One body cell (spec M1-B4 §8.3, `[B5§6.1]`).
 *
 * The cell owns nothing about the value: it asks the registry for the renderer and, while
 * editing, for the editor. What it owns is the selection chrome (focus ring, range tint,
 * presence outline) and the click-to-focus / click-again-to-edit rule.
 */
import type { ColumnId } from '@elizmonday/shared';
import { memo, type MouseEvent as ReactMouseEvent } from 'react';
import { CellEditorBoundary } from '../columns/primitives';
import { getColumnUi } from '../columns/registry';
import type { CellRenderContext } from '../columns/types';
import type { BoardColumn, ItemRow } from '../data/types';
import type { CellAddress } from './selection';

export interface GridCellProps {
  column: BoardColumn;
  item: ItemRow;
  context: CellRenderContext;
  left?: number;
  width: number;
  colIndex: number;
  focused: boolean;
  inRange: boolean;
  editing: boolean;
  readOnly: boolean;
  /** 6i / q455: `Wrap text` is on for this column, so the cell's text wraps (two lines). */
  wrapped?: boolean;
  initialInput?: string;
  /** Another viewer's focus, as a 2 px outline in their avatar colour (§8.12). */
  presenceColor?: string;
  onFocusCell(address: CellAddress, event: ReactMouseEvent): void;
  onStartEditing(address: CellAddress): void;
  onCommit(address: CellAddress, value: unknown): void;
  onCancel(): void;
  onNavigate(direction: 'up' | 'down' | 'left' | 'right' | 'next' | 'prev'): void;
}

function valueOf(item: ItemRow, columnId: ColumnId): unknown {
  return columnId === 'name' ? item.name : (item.values[columnId] ?? null);
}

export const GridCell = memo(function GridCell({
  column,
  item,
  context,
  left,
  width,
  colIndex,
  focused,
  inRange,
  editing,
  readOnly,
  wrapped,
  initialInput,
  presenceColor,
  onFocusCell,
  onStartEditing,
  onCommit,
  onCancel,
  onNavigate,
}: GridCellProps) {
  const ui = getColumnUi(column.type);
  const address: CellAddress = { itemId: item.id, columnId: column.id };
  const value = valueOf(item, column.id);
  const Editor = ui.Editor;

  /*
   * `data-column-type` states the type the id cannot carry. An imported board's column ids are
   * arbitrary (`packages/shared/src/columns/column-ids.ts`: Italian, legacy type names,
   * `auto_number0`), so anything outside the grid that needs the type — `tools/qa/sweep.mjs`'s
   * open-rule, which must not expect an editor from a type that has none — had to guess it from
   * the id and guessed wrong. It is one attribute beside `data-column`, not a `switch (type)`.
   */
  return (
    <div
      className="ez-cell"
      role="gridcell"
      aria-colindex={colIndex + 1}
      aria-selected={inRange || undefined}
      aria-readonly={readOnly || undefined}
      data-column={column.id}
      data-column-type={column.type}
      data-align={ui.align}
      data-focused={focused || undefined}
      data-editing={editing || undefined}
      data-wrap={wrapped || undefined}
      data-range={inRange || undefined}
      data-testid={`cell-${item.id}-${column.id}`}
      tabIndex={-1}
      style={{
        ...(left === undefined ? { width } : { position: 'absolute', left, width }),
        ...(presenceColor ? { outline: `2px solid ${presenceColor}`, outlineOffset: '-2px' } : {}),
      }}
      onMouseDown={(event) => {
        /*
         * **A mousedown inside the cell that is already editing changes nothing.**
         *
         * A popover editor renders *inside* the cell, so its own mousedown bubbles here; so
         * does the caret-placing mousedown in an inline `input.ez-cell__input`. Either one used
         * to reach `onFocusCell` → `stopEditing()`, which unmounted the editor before its own
         * `click` handler ever ran: every pick in the status, dropdown, date and people pickers
         * was lost (found while writing the P-003 test), and — P-019, round 7e — clicking the
         * open name/text/numbers editor a second time, or anywhere in the padding beside its
         * input, simply **closed** it. monday keeps the editor open: the click is inside the
         * thing you are editing.
         *
         * The guard used to be `editing && target.closest('.ez-cell-popover')`, which covered
         * only the in-cell popovers and left every inline editor exposed. While this cell is
         * editing, the editor owns its own mousedowns; a click on *another* cell is that
         * cell's `onFocusCell`, which still stops this one.
         */
        if (editing) return;
        /*
         * "First click selects, second click edits" is monday's measured rule for every type
         * that has an editor (`discovery/ui-reference/editors/open-rule.json`,
         * `<type>/inline-behaviour.json`: `openedBy: "click2"` for status, dropdown, date,
         * long_text, text, numbers and name alike).
         *
         * `checkbox` is the one type that acts on the first click, because it has no editor
         * to open — the click *is* the write. The type declares that with
         * `activateOnFirstClick`; the grid never asks what type it is.
         */
        /*
         * A read-only cell opens nothing — except the three types whose editor is a **panel**
         * and not a value editor (`file`, `time_tracking`, `mirror`), which monday opens on a
         * read-only column too. The type declares it with `opensWhenReadOnly`; the grid still
         * never asks what type it is (M2 tier-2 §6).
         */
        if (
          !editing &&
          Editor &&
          (!readOnly || ui.opensWhenReadOnly === true) &&
          (focused || ui.activateOnFirstClick)
        ) {
          event.preventDefault();
          if (!focused) onFocusCell(address, event);
          onStartEditing(address);
          return;
        }
        onFocusCell(address, event);
      }}
    >
      {editing && Editor ? (
        /*
         * An editor that throws must cost a closed popover, never the board route. Without
         * this, one bad line in one type's editor (round 5t: `mirror` reading the wrong
         * serialisation of `displayed_linked_columns`) unwound all the way to the router's
         * `errorElement` and replaced the whole screen with *Something went wrong*. The
         * boundary catches, records the crash and asks the grid to leave edit mode, so the
         * cell falls back to its renderer with the selection intact.
         */
        <CellEditorBoundary
          columnId={column.id}
          columnType={column.type}
          resetKey={`${item.id}:${column.id}`}
          onClose={onCancel}
        >
          <Editor
            value={value}
            settings={column.settings}
            column={column}
            item={item}
            context={context}
            readOnly={readOnly}
            autoFocus
            initialInput={initialInput}
            onCommit={(next) => onCommit(address, next)}
            onCancel={onCancel}
            onNavigate={onNavigate}
          />
        </CellEditorBoundary>
      ) : (
        <ui.Cell value={value} settings={column.settings} column={column} item={item} context={context} />
      )}
    </div>
  );
});
